import get from 'lodash/get'
import isArray from 'lodash/isArray'
import toPairs from 'lodash/toPairs'
import type { TLocalizedValidationError } from 'typebox/error'
import { MONEY_FORMAT } from '@klear/money/format'

export type WireValidationIssue = {
  readonly path: string
  readonly message: string
}

const ENUM_BRANCH_MESSAGES = new Set([
  'must be equal to constant',
  'must be equal to one of the allowed values',
  'must be string'
])

export function instancePathToFieldPath(instancePath: string): string {
  if (!instancePath || instancePath === '/') {
    return 'root'
  }
  return instancePath.replace(/^\//, '').replace(/\//g, '.')
}

export function formatWireValidationIssues(errors: Iterable<TLocalizedValidationError>): WireValidationIssue[] {
  const issues: WireValidationIssue[] = []
  for (const error of errors) {
    const extra = error.keyword === 'additionalProperties' ? get(error.params, 'additionalProperties') : undefined
    issues.push({
      path: instancePathToFieldPath(error.instancePath),
      message: isArray(extra) && extra.length > 0 ? `${error.message}: ${extra.join(', ')}` : error.message
    })
  }
  return issues
}

function preferWireValidationMessage(current: string, candidate: string): string {
  if (ENUM_BRANCH_MESSAGES.has(current) && !ENUM_BRANCH_MESSAGES.has(candidate)) {
    return candidate
  }
  return current
}

export function dedupeWireValidationIssues(issues: readonly WireValidationIssue[]): WireValidationIssue[] {
  const byPath = new Map<string, WireValidationIssue>()
  for (const issue of issues) {
    const existing = byPath.get(issue.path)
    if (!existing) {
      byPath.set(issue.path, issue)
      continue
    }
    byPath.set(issue.path, {
      path: issue.path,
      message: preferWireValidationMessage(existing.message, issue.message)
    })
  }
  return [...byPath.values()]
}

const MONEY_FAILURE_MESSAGE =
  'Write money as the ISO code, a space, and the figure at that currency’s scale, such as USD 1250.00'

const PATTERN_FAILURE_MESSAGES: Record<string, string> = {
  lei: 'LEI must be a valid ISO 17442 identifier',
  dunsNumber: 'DUNS number must be 9 digits',
  ein: 'EIN must be 9 digits (format NN-NNNNNNN)',
  vatNumber: 'VAT number format is invalid',
  amount: MONEY_FAILURE_MESSAGE
}

function matchesFieldPath(path: string, field: string): boolean {
  return path === field || path.endsWith(`.${field}`)
}

export function humanizeWireValidationMessage(path: string, message: string): string {
  if (message === `must match format "${MONEY_FORMAT}"`) return MONEY_FAILURE_MESSAGE
  if (path === 'root' || path.endsWith('.country') || path === 'country') {
    if (ENUM_BRANCH_MESSAGES.has(message)) {
      return 'Country is required'
    }
  }
  if (message.startsWith('must match pattern')) {
    for (const [field, friendly] of toPairs(PATTERN_FAILURE_MESSAGES)) {
      if (matchesFieldPath(path, field)) {
        return friendly
      }
    }
  }
  return message
}

export function formatWireValidationIssuesLabeled(issues: readonly WireValidationIssue[]): string {
  return dedupeWireValidationIssues(issues)
    .map((issue) => {
      const message = humanizeWireValidationMessage(issue.path, issue.message)
      return issue.path === 'root' ? message : `${issue.path}: ${message}`
    })
    .join('; ')
}

export function formatWireValidationMessage(issues: readonly WireValidationIssue[]): string {
  if (issues.length === 0) {
    return 'Validation failed.'
  }
  return issues.map((issue) => `${issue.path} ${issue.message}`).join('; ')
}
